import { computed, inject, signal } from '@angular/core';
import { LogsService } from '@services/logs';

/**
 * Abstract generic store base class for managing reactive state using Angular signals.
 * @template T Type representing the shape of the state object managed by the store.
 */
export abstract class StoreBase<T> {
  // Name used for logging identification. Should be overridden in subclasses.
  storeName: string = 'Store';

  readonly #logsService = inject(LogsService);

  #store = signal<T>(null!);
  read = this.#store.asReadonly();

  /**
   * Updates the internal store state and logs the change with contextual store name.
   * Warns if the store name has not been overridden in subclasses.
   * @param data New state data of type T to set into the store.
   */
  set(data: T): void {
    this.#store.set(data);
    if (this.storeName === 'Store') {
      this.#logsService.warn('StoreBase', 'Store name is not defined', data);
    } else {
      this.#logsService.debug(this.storeName, 'State changed', data);
    }
  }

  /**
   * Returns a computed signal selecting a specific property from the current state by key.
   * @param key The state property key to observe.
   * @returns A computed signal for the selected state property.
   */
  select<K extends keyof T>(key: K) {
    return computed(() => this.#store()?.[key]);
  }

  /**
   * Returns a computed signal by applying a provided selector function onto the current state.
   * Useful for accessing deeply nested or computed properties.
   * @param selector Function to select desired properties or perform transformations on state.
   * @returns A computed signal of the selected or transformed state data.
   */
  selectPath<R>(selector: (state: T) => R) {
    return computed(() => {
      const state = this.#store();
      return state ? selector(state) : undefined;
    });
  }
}
